import type { FunctionComponent } from 'react';

import { isMemberLoggedIn } from '@trello/authentication';
import { token } from '@trello/theme';

import { LogoTrello } from 'app/src/components/LoggedOutHeader/LogoTrello';

// eslint-disable-next-line @trello/assets-alongside-implementation
import url from 'resources/images/header-logo-spirit-loading.gif';

export const LoggedOutHeaderSkeleton: FunctionComponent = () => (
  <div
    data-testid="logged-out-skeleton-header"
    style={{
      minHeight: 60,
      maxHeight: 60,
      background: token('elevation.surface', '#FFFFFF'),
      display: 'flex',
    }}
  >
    <div
      style={{
        display: 'flex',
        flexGrow: 1,
        alignItems: 'center',
      }}
    >
      <div style={{ margin: `${token('space.0', '0px')} 14px` }}>
        <LogoTrello height={22.5} navSize="big" />
      </div>
      <div
        style={{
          alignItems: 'stretch',
          display: 'flex',
          flex: '1 0 0',
          padding: `${token('space.250', '20px')} ${token('space.0', '0px')} 14px ${token('space.0', '0px')}`,
        }}
      >
        <div
          style={{
            backgroundColor: token('color.skeleton.subtle', '#091E4208'),
            height: 29,
            width: 104,
            borderRadius: token('border.radius.100', '3px'),
            margin: `14px ${token('space.025', '2px')}`,
          }}
        />
        <div
          style={{
            backgroundColor: token('color.skeleton.subtle', '#091E4208'),
            height: 29,
            width: 109,
            borderRadius: token('border.radius.100', '3px'),
            margin: `14px ${token('space.025', '2px')}`,
          }}
        />
        <div
          style={{
            backgroundColor: token('color.skeleton.subtle', '#091E4208'),
            height: 29,
            width: 78,
            borderRadius: token('border.radius.100', '3px'),
            margin: `14px ${token('space.025', '2px')}`,
          }}
        />
        <div
          style={{
            backgroundColor: token('color.skeleton.subtle', '#091E4208'),
            height: 29,
            width: 78,
            borderRadius: token('border.radius.100', '3px'),
            margin: `14px ${token('space.025', '2px')}`,
          }}
        />
        <div
          style={{
            backgroundColor: token('color.skeleton.subtle', '#091E4208'),
            height: 29,
            width: 115,
            borderRadius: token('border.radius.100', '3px'),
            margin: `14px ${token('space.025', '2px')}`,
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexGrow: 1,
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <div
          style={{
            backgroundColor: token('color.skeleton.subtle', '#091E4208'),
            height: 32,
            width: 65,
            borderRadius: token('border.radius.100', '3px'),
            margin: `${token('space.0', '0px')} ${token('space.200', '16px')}`,
          }}
        />
        <div
          style={{
            backgroundColor: token('color.background.brand.bold', '#0C66E4'),
            height: 60,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: token(
                'color.background.selected.pressed',
                'hsla(0, 0%, 100%, 0.3)',
              ),
              height: 32,
              width: 170,
              borderRadius: token('border.radius.100', '3px'),
              margin: `${token('space.0', '0px')} ${token('space.200', '16px')}`,
            }}
          />
        </div>
      </div>
    </div>
  </div>
);

interface HeaderSkeletonProps {
  backgroundColor?: string;
}

const defaultBackgroundColor = token(
  'color.background.neutral.bold',
  '#44546F',
);

export const LoggedInHeaderSkeleton: FunctionComponent<HeaderSkeletonProps> = ({
  backgroundColor = defaultBackgroundColor,
}) => (
  <div
    data-testid="logged-in-skeleton-header"
    style={{
      minHeight: 44,
      maxHeight: 44,
      backgroundColor,
    }}
  >
    <div
      style={{
        backgroundColor,
        height: 32,
        padding: `${token('space.075', '6px')} ${token('space.050', '4px')}`,
        display: 'flex',
      }}
    >
      <div style={{ display: 'flex', flexGrow: 1 }}>
        <div
          style={{
            backgroundColor: token(
              'color.background.accent.gray.subtle',
              'hsla(0, 0%, 100%, 0.3)',
            ),
            height: 32,
            width: 32,
            borderRadius: token('border.radius.100', '3px'),
            marginRight: token('space.050', '4px'),
          }}
        />
        <div
          style={{
            backgroundImage: `url(${url})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            height: 32,
            width: 75,
            margin: `7px ${token('space.100', '8px')} ${token('space.0', '0px')} ${token('space.025', '2px')}`,
            opacity: 0.5,
            position: 'relative',
            padding: `${token('space.100', '8px')} ${token('space.0', '0px')}`,
          }}
        />
        <div
          style={{
            alignItems: 'stretch',
            display: 'flex',
            flexGrow: 1,
            flexShrink: 0,
            flexBasis: 0,
            height: '100%',
            position: 'relative',
          }}
        >
          <div
            style={{
              display: 'flex',
              margin: `${token('space.0', '0px')} ${token('space.050', '4px')}`,
            }}
          >
            <div
              style={{
                backgroundColor: token(
                  'color.background.accent.gray.subtle',
                  'hsla(0, 0%, 100%, 0.3)',
                ),
                height: 32,
                width: 124,
                borderRadius: token('border.radius.100', '3px'),
                marginRight: token('space.050', '4px'),
              }}
            />
            <div
              style={{
                backgroundColor: token(
                  'color.background.accent.gray.subtle',
                  'hsla(0, 0%, 100%, 0.3)',
                ),
                height: 32,
                width: 90,
                borderRadius: token('border.radius.100', '3px'),
                marginRight: token('space.050', '4px'),
              }}
            />
            <div
              style={{
                backgroundColor: token(
                  'color.background.accent.gray.subtle',
                  'hsla(0, 0%, 100%, 0.3)',
                ),
                height: 32,
                width: 90,
                borderRadius: token('border.radius.100', '3px'),
                marginRight: token('space.050', '4px'),
              }}
            />
            <div
              style={{
                backgroundColor: token(
                  'color.background.accent.gray.subtle',
                  'hsla(0, 0%, 100%, 0.3)',
                ),
                height: 32,
                width: 110,
                borderRadius: token('border.radius.100', '3px'),
                marginRight: token('space.050', '4px'),
              }}
            />
            <div
              style={{
                backgroundColor: token(
                  'color.background.accent.gray.subtle',
                  'hsla(0, 0%, 100%, 0.3)',
                ),
                height: 32,
                width: 68,
                borderRadius: token('border.radius.100', '3px'),
                margin: `${token('space.0', '0px')} ${token('space.050', '4px')} ${token('space.0', '0px')} ${token('space.150', '12px')}`,
              }}
            />
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexGrow: 1, justifyContent: 'flex-end' }}>
        <div
          style={{
            backgroundColor: token(
              'color.background.accent.gray.subtle',
              'hsla(0, 0%, 100%, 0.3)',
            ),
            height: 32,
            width: 250,
            borderRadius: token('border.radius.100', '3px'),
            marginRight: token('space.050', '4px'),
          }}
        />
        <div
          style={{
            backgroundColor: token(
              'color.background.accent.gray.subtle',
              'hsla(0, 0%, 100%, 0.3)',
            ),
            height: 32,
            width: 32,
            borderRadius: token('border.radius.100', '3px'),
            marginRight: token('space.050', '4px'),
          }}
        />
        <div
          style={{
            backgroundColor: token(
              'color.background.accent.gray.subtle',
              'hsla(0, 0%, 100%, 0.3)',
            ),
            height: 32,
            width: 32,
            borderRadius: token('border.radius.100', '3px'),
            marginRight: token('space.050', '4px'),
          }}
        />
        <div
          style={{
            backgroundColor: token(
              'color.background.accent.gray.subtle',
              'hsla(0, 0%, 100%, 0.3)',
            ),
            height: 32,
            width: 32,
            borderRadius: token('border.radius.100', '3px'),
            marginRight: token('space.050', '4px'),
          }}
        />
        <div
          style={{
            backgroundColor: token(
              'color.background.accent.gray.subtle',
              'hsla(0, 0%, 100%, 0.3)',
            ),
            height: 32,
            width: 32,
            borderRadius: token('border.radius.100', '3px'),
            marginRight: token('space.050', '4px'),
          }}
        />
        <div
          style={{
            backgroundColor: token(
              'color.background.accent.gray.subtle',
              'hsla(0, 0%, 100%, 0.3)',
            ),
            height: 32,
            width: 32,
            borderRadius: token('border.radius.circle', '50%'),
          }}
        />
      </div>
    </div>
  </div>
);

export const HeaderSkeleton: FunctionComponent<HeaderSkeletonProps> = (
  props,
) =>
  isMemberLoggedIn() ? (
    <LoggedInHeaderSkeleton {...props} />
  ) : (
    <LoggedOutHeaderSkeleton />
  );
